import React, { useState, useEffect } from 'react'
import {
  Edit,
  SimpleForm,
  useNotify,
  useRedirect,
  useTranslate,
  FormDataConsumer,
  BooleanInput,
  useUpdate,
  useEditController,
} from 'react-admin'
import { Grid, Typography, Box, Button, IconButton, Divider, Modal, Skeleton } from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { useQuery } from 'react-query'
import { CardCommon } from 'src/components/uiElements'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { TextInput } from 'src/components/uiElements/fields/TextInput'
import { NumberInput } from 'src/components/uiElements/fields/NumberInput'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { DatePickers } from 'src/components/uiElements/react-hook-fields'
import { TreeViewCheck } from 'src/components/uiElements/styledComponents/TreeViewCheck'
import { workUnitServices } from 'src/services/reservation/workUnit/WorkUnit.service'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { businessRuleServices } from 'src/services/sales/businessRule/businessRule.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import dayjs from 'dayjs'

const RangesArray = ({ initialRanges }) => {
  const translate = useTranslate()
  const { control, watch, setValue } = useFormContext()
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'ranges',
  })

  const ranges = watch('ranges')

  useEffect(() => {
    if (initialRanges && initialRanges.length > 0) {
      replace(initialRanges)
    } else if (fields.length === 0) {
      append({ from: '', to: '', type: '', discount: '', cap: '' })
    }
  }, [initialRanges])

  useEffect(() => {
    if (ranges && Array.isArray(ranges)) {
      ranges.forEach((range, index) => {
        if (range.type === 'fixed' && range.cap) {
          setValue(`ranges.${index}.cap`, '')
        }
      })
    }
  }, [ranges?.map(r => r?.type).join(',')])

  const rangeTypes = [
    { id: 'fixed', name: translate('ra.field.fixed_amount') },
    { id: 'percentage', name: translate('ra.field.percentage') },
  ]

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(194, 194, 194, 0.13)',
        borderRadius: 1,
        p: 2,
        mt: 2,
      }}
    >
      <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>
        {translate('ra.field.ranges').toUpperCase()}
      </Typography>

      {fields.map((field, index) => (
        <Grid container spacing={2} key={field.id} sx={{ mb: 2, alignItems: 'center' }}>
          <Grid item xs={12} sm={2}>
            <Typography variant='caption' sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
              {translate('ra.field.from')}
            </Typography>
            <TextInput source={`ranges.${index}.from`} label='' fullWidth sx={{ backgroundColor: 'white' }} />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Typography variant='caption' sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
              {translate('ra.field.to')}
            </Typography>
            <TextInput source={`ranges.${index}.to`} label='' fullWidth sx={{ backgroundColor: 'white' }} />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <Typography variant='caption' sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
              {translate('ra.field.type')}
            </Typography>
            <SelectInput
              source={`ranges.${index}.type`}
              label=''
              fullWidth
              options={rangeTypes}
              optionsText={['name']}
              displayEmpty
              sx={{ backgroundColor: 'white' }}
              renderValue={(selected) => {
                if (!selected) {
                  return translate('ra.action.select')
                }
                const option = rangeTypes.find((opt) => opt.id === selected)
                return option ? option.name : selected
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Typography variant='caption' sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
              {translate('ra.field.discount')}
            </Typography>
            <TextInput source={`ranges.${index}.discount`} label='' fullWidth sx={{ backgroundColor: 'white' }} />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Typography variant='caption' sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
              {translate('ra.field.cap')}
            </Typography>
            <FormDataConsumer>
              {({ formData }) => {
                const isDisabled = formData?.ranges?.[index]?.type === 'fixed'
                return (
                  <TextInput 
                    source={`ranges.${index}.cap`} 
                    label='' 
                    fullWidth 
                    disabled={isDisabled}
                    sx={{ 
                      backgroundColor: isDisabled ? 'rgb(247 247 247)' : 'white',
                      '& .MuiInputBase-input.Mui-disabled': {
                        backgroundColor: 'rgb(247 247 247)',
                        WebkitTextFillColor: '#9e9e9e'
                      }
                    }} 
                  />
                )
              }}
            </FormDataConsumer>
          </Grid>

          <Grid item xs={12} sm={1.5} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
            <IconButton onClick={() => remove(index)} color='error' sx={{ mt: 2 }}>
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      ))}

      <Button startIcon={<Add />} onClick={() => append({ from: '', to: '', type: '', discount: '', cap: '' })} sx={{ mt: 1 }}>
        {translate('ra.field.add_range')}
      </Button>
    </Box>
  )
}

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 600,
  maxHeight: '80vh',
  overflow: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
}

const SiteCurrencyField = ({ destinationTypes, siteCurrencies }) => {
  const translate = useTranslate()
  const { watch, setValue } = useFormContext()
  const destinationTypeId = watch('destination_type_id')
  const siteCurrency = watch('site_currency')

  const destinationType = destinationTypes.data?.find(dt => dt.id === destinationTypeId)
  const isNacional = destinationType?.name === 'Nacional'
  const filteredCurrencies = isNacional ? [{ id: 'ARS', name: 'ARS' }] : siteCurrencies

  useEffect(() => {
    if (isNacional && siteCurrency !== 'ARS') {
      setValue('site_currency', 'ARS')
    }
  }, [isNacional, siteCurrency, setValue])

  return (
    <SelectInput
      source="site_currency"
      label=""
      fullWidth
      options={filteredCurrencies}
      optionsText={['name']}
      displayEmpty
      disabled={isNacional}
      renderValue={(selected) => {
        if (!selected || selected === '') {
          return <span style={{ color: '#999' }}>Seleccionar</span>;
        }
        return filteredCurrencies.find(c => c.id === selected)?.name;
      }}
      sx={{ 
        backgroundColor: isNacional ? 'rgb(247 247 247)' : 'white',
        '& .MuiInputBase-input.Mui-disabled': {
          backgroundColor: 'rgb(247 247 247)',
          WebkitTextFillColor: '#9e9e9e'
        }
      }}
    />
  )
}

const AirlineSelectField = ({ airAirlines, loadingAirAirlines }) => {
  const translate = useTranslate()
  const { watch, setValue } = useFormContext()
  const airlineCode = watch('airline_code')

  const handleAirlineChange = (e) => {
    const newValue = e.target.value
    const currentValue = airlineCode || []

    if (newValue.includes('YY') && !currentValue.includes('YY')) {
      setValue('airline_code', ['YY'])
    }
    else if (currentValue.includes('YY') && newValue.length > 1) {
      setValue('airline_code', newValue.filter(code => code !== 'YY'))
    }
    else {
      setValue('airline_code', newValue)
    }
  }

  if (loadingAirAirlines) {
    return <Skeleton variant='rectangular' width={'100%'} height={40} />
  }

  return (
    <SelectInput
      source='airline_code'
      label=''
      fullWidth
      options={airAirlines}
      optionsText={['long_name']}
      optionValue='short_name'
      multiple
      showSearch
      limitOptions={false}
      displayEmpty
      onChange={handleAirlineChange}
      renderValue={(selected) => {
        if (!selected || selected.length === 0) {
          return translate('ra.action.select')
        }
        return selected
          .map((value) => {
            const option = airAirlines.find((opt) => opt.short_name === value)
            return option ? option.long_name : value
          })
          .join(', ')
      }}
    />
  )
}

const DestinationTypeField = ({ destinationTypes }) => {
  const translate = useTranslate()
  const { watch, setValue } = useFormContext()
  const destinationTypeId = watch('destination_type_id')

  useEffect(() => {
    if (!destinationTypeId && destinationTypes.data) {
      const todosOption = destinationTypes.data.find(dt => dt.name === 'Todos')
      if (todosOption) {
        setValue('destination_type_id', todosOption.id)
      }
    }
  }, [destinationTypes.data, destinationTypeId, setValue])

  if (destinationTypes.isLoading) {
    return <Skeleton variant='rectangular' width={'100%'} height={40} />
  }

  return (
    <SelectInput
      source='destination_type_id'
      label=''
      fullWidth
      options={destinationTypes.data || []}
      optionsText={['name']}
    />
  )
}

const RequiredServicesField = ({ requiredServicesList }) => {
  const translate = useTranslate()
  const { watch, setValue } = useFormContext()
  const requiredServices = watch('required_services')

  const handleServicesChange = (e) => {
    const newValue = e.target.value
    const currentValue = requiredServices || []

    const allServiceIds = requiredServicesList
      .filter(service => service.id !== 'all')
      .map(service => service.id)
    const allCurrentlySelected = allServiceIds.every(id => currentValue.includes(id)) && currentValue.length === allServiceIds.length

    if (newValue.includes('all') && allCurrentlySelected) {
      setValue('required_services', [])
    }
    else if (newValue.includes('all') && !allCurrentlySelected) {
      setValue('required_services', allServiceIds)
    }
    else {
      setValue('required_services', newValue.filter(s => s !== 'all'))
    }
  }

  // Verificar si todos los servicios están seleccionados
  const allSelected = requiredServices?.length === requiredServicesList.length - 1

  return (
    <SelectInput
      source='required_services'
      label=''
      fullWidth
      options={requiredServicesList}
      optionsText={['name']}
      multiple
      displayEmpty
      onChange={handleServicesChange}
      renderValue={(selected) => {
        if (!selected || selected.length === 0) {
          return translate('ra.action.select')
        }
        // Si todos están seleccionados, mostrar "Todos"
        if (selected.length === requiredServicesList.length - 1) {
          return translate('ra.field.all')
        }
        return selected
          .map((value) => {
            const option = requiredServicesList.find((opt) => opt.id === value)
            return option ? option.name : value
          })
          .join(', ')
      }}
    />
  )
}

export const PromotionEdit = (props) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const [update] = useUpdate()
  const { record, isLoading } = useEditController()

  const [openWorkUnitModal, setOpenWorkUnitModal] = useState(false)
  const [currentWorkUnitToken, setCurrentWorkUnitToken] = useState([])
  const [currentWorkUnitName, setCurrentWorkUnitName] = useState('')
  const [expanded, setExpanded] = useState([])

  const [loadingProductType, setLoadingProductType] = useState(false)
  const [productTypes, setProductTypes] = useState([])
  const [selectedProductType, setSelectedProductType] = useState(null)

  const [airAirlines, setAirAirlines] = useState([])
  const [loadingAirAirlines, setLoadingAirAirlines] = useState(false)

  const [transformedRecord, setTransformedRecord] = useState(null)

  const allWorkUnits = useQuery('work-unit-all', () => workUnitServices.getByOrderParent())
  
  const destinationTypes = useQuery('destination-type-all', businessRuleServices.getDestinationTypes)
  
  const currencies = useQuery('currency-all', currencyServices.getByAll)

  const getProductType = async () => {
    try {
      setLoadingProductType(true)
      const product_type = await productTypeServices.getByParam({ limit: 999 })
      if (product_type) {
        const remove_product_type_other = product_type.filter(
          (elem) => elem.ref_table_detail !== 'other_detail'
        )
        remove_product_type_other.push({
          ref_table_detail: 'other_detail',
          short_name: 'Otros',
          long_name: 'Otros',
          id: 9999,
        })
        setProductTypes(remove_product_type_other)
      } else {
        setProductTypes([])
      }
    } catch (error) {
      setProductTypes([])
    } finally {
      setLoadingProductType(false)
    }
  }

  const getAirlines = async () => {
    try {
      setLoadingAirAirlines(true)
      const allAirlines = await businessRuleServices.getAirlines()
      // Ordenar alfabéticamente
      const sortedAirlines = allAirlines.sort((a, b) => 
        (a.long_name || '').localeCompare(b.long_name || '')
      )
      // Mover "Todas las Aerolineas" (YY) al principio y cambiar su nombre a "Todas"
      const allOption = sortedAirlines.find(airline => airline.short_name === 'YY')
      if (allOption) {
        allOption.long_name = 'Todas'
      }
      const otherAirlines = sortedAirlines.filter(airline => airline.short_name !== 'YY')
      const finalList = allOption ? [allOption, ...otherAirlines] : sortedAirlines
      setAirAirlines(finalList)
      setLoadingAirAirlines(false)
    } catch (error) {
      setAirAirlines([])
      setLoadingAirAirlines(false)
    }
  }

  const requiredServicesList = [
    { id: 'all', name: translate('ra.field.all') },
    { id: 'flexi', name: translate('ra.field.service_flexible') },
    { id: 'branded_fare', name: translate('ra.field.service_branded_fare') },
    { id: 'ancillary_baggage', name: translate('ra.field.service_extra_baggage') },
    { id: 'extra_services', name: translate('ra.field.service_hepstar') },
    { id: 'service_package', name: translate('ra.field.service_premium_package') },
  ]

  const applicationValues = [
    { id: 'base_fare', name: translate('ra.field.application_base_fare') },
    { id: 'base_fare_taxes', name: translate('ra.field.application_base_fare_taxes') },
    { id: 'base_fare_taxes_charges', name: translate('ra.field.application_base_fare_taxes_charges') },
    { id: 'total_amount', name: translate('ra.field.application_total_amount') },
    { id: 'flexi_amount', name: translate('ra.field.application_flexi') },
    { id: 'service_package_amount', name: translate('ra.field.application_service_package') },
  ]

  const rangeCurrencies = [
    { id: 'USD', name: 'USD' },
    { id: 'ARS', name: 'ARS' },
  ]

  const siteCurrencies = [
    { id: 'all', name: translate('ra.field.all') },
    { id: 'USD', name: 'USD' },
    { id: 'ARS', name: 'ARS' },
  ]

  const paymentMethods = [
    { id: translate('ra.field.credit_card'), name: translate('ra.field.credit_card') },
    { id: translate('ra.field.debit_card'), name: translate('ra.field.debit_card') },
    { id: translate('ra.field.debin'), name: translate('ra.field.debin') },
    { id: translate('ra.field.deposit'), name: translate('ra.field.deposit') },
    { id: translate('ra.field.transfer'), name: translate('ra.field.transfer') },
  ]

  useEffect(() => {
    getProductType()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Cargar aerolíneas cuando se selecciona tipo de producto Aéreos
  useEffect(() => {
    if (selectedProductType) {
      const productTypeObj = productTypes.find(pt => pt.id === selectedProductType)
      if (productTypeObj?.ref_table_detail === 'air_detail') {
        if (airAirlines.length === 0) {
          getAirlines()
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductType])

  useEffect(() => {
    if (allWorkUnits.data && currentWorkUnitToken.length > 0) {
      const getWorkUnitsByTokens = (tokens, nodes) => {
        const result = []
        const findNodes = (nodeList) => {
          for (const node of nodeList) {
            if (tokens.includes(node.value)) {
              result.push(node)
            }
            if (node.children && node.children.length > 0) {
              findNodes(node.children)
            }
          }
        }
        findNodes(nodes)
        return result
      }

      const selectedWorkUnits = getWorkUnitsByTokens(currentWorkUnitToken, allWorkUnits.data)
      const names = selectedWorkUnits.map(wu => wu.long_name || wu.label).join(', ')
      setCurrentWorkUnitName(names)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkUnitToken, allWorkUnits.data])

  useEffect(() => {
    if (record && productTypes.length > 0 && allWorkUnits.data && destinationTypes.data && currencies.data && !transformedRecord) {
      // Transformar datos del API al formato del formulario
      const ranges =
        record.pricing_rules?.ranges?.map((range) => ({
          from: range.min_value?.toString() || '',
          to: range.max_value?.toString() || '',
          type: range.type_discount || '',
          discount: range.discount_value?.toString() || '',
          cap: range.max_limit?.toString() || '',
        })) || [{ from: '', to: '', type: '', discount: '', cap: '' }]


      let appliedOn = record.applied_on
      if (appliedOn === 'fare_amount') appliedOn = 'base_fare'
      else if (appliedOn === 'fare_amount_with_taxes') appliedOn = 'base_fare_taxes'
      else if (appliedOn === 'fare_amount_with_taxes_charges') appliedOn = 'base_fare_taxes_charges'

      const productTypeId = record.product_type?.id || record.product_type_id
      setSelectedProductType(productTypeId)
      
      const workUnitId = record.work_unit?.id || record.work_unit_id
      if (workUnitId) {
        const findWorkUnitValue = (nodes, targetId) => {
          for (const node of nodes) {
            if (node.id === targetId) return node.value
            if (node.children?.length > 0) {
              const found = findWorkUnitValue(node.children, targetId)
              if (found) return found
            }
          }
          return null
        }
        const value = findWorkUnitValue(allWorkUnits.data, workUnitId)
        if (value) {
          setCurrentWorkUnitToken([value])
          const findWorkUnitByValue = (nodes, targetValue) => {
            for (const node of nodes) {
              if (node.value === targetValue) return node
              if (node.children?.length > 0) {
                const found = findWorkUnitByValue(node.children, targetValue)
                if (found) return found
              }
            }
            return null
          }
          const workUnit = findWorkUnitByValue(allWorkUnits.data, value)
          if (workUnit) {
            setCurrentWorkUnitName(workUnit.long_name || workUnit.label)
          }
        }
      }

      let destinationTypeId = null
      if (record.product_rules?.destination_type) {
        const destType = destinationTypes.data?.find(dt => dt.name === record.product_rules.destination_type)
        if (destType) {
          destinationTypeId = destType.id
        }
      }
      let currencyShortName = 'all'
      if (record.currency && currencies.data) {
        const currencyObj = currencies.data.find(c => c.id === record.currency.id)
        if (currencyObj && currencyObj.short_name) {
          currencyShortName = currencyObj.short_name
        }
      }

      const newTransformedRecord = {
        ...record,
        name: record.name || '',
        site_description: record.description || '',
        apply_coupon: record.coupon_based || false,
        coupon_code: record.coupons?.[0]?.code || '',
        total_quantity: record.coupons?.[0]?.usage_limit || '',
        product_type: productTypeId || null,
        airline_code: record.product_rules?.airline === null ? ['YY'] : (record.product_rules?.airline || []),
        destination_type_id: destinationTypeId,
        required_services: record.services_included || [],
        application_value: appliedOn,
        range_currency: record.pricing_rules?.currency_code || '',
        ranges,
        validity_start: record.starts_at,
        validity_end: record.ends_at,
        service_start_from: record.check_in,
        service_end_to: record.check_out,
        site_currency: currencyShortName,
        payment_method: record.payment_method || '',
        promotion_conditions: record.terms_and_conditions_url || '',
      }

      setTransformedRecord(newTransformedRecord)
    }
  }, [record, productTypes, allWorkUnits.data, destinationTypes.data, currencies.data])

  const handleOpenWorkUnitModal = () => setOpenWorkUnitModal(true)
  const handleCloseWorkUnitModal = () => setOpenWorkUnitModal(false)

  const onSuccess = () => {
    notify('ra.notification.updated_success_feminine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.promotion') },
    })
    redirect('/promotion')
  }

  const onError = () => {
    notify('ra.notification.updated_error_feminine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.promotion') },
    })
  }

  const transformFormData = (formData) => {
    const appliedOnMap = {
      base_fare: 'fare_amount',
      base_fare_taxes: 'fare_amount_with_taxes',
      base_fare_taxes_charges: 'fare_amount_with_taxes_charges',
    }

    const ranges = (formData.ranges || []).map(range => {
      const rangeObj = {
        min_value: parseFloat(range.from) || 0,
        type_discount: range.type || 'fixed',
        discount_value: parseFloat(range.discount) || 0,
      }
      
      if (range.to) {
        rangeObj.max_value = parseFloat(range.to)
      }
      
      if (range.cap) {
        rangeObj.max_limit = parseFloat(range.cap)
      }
      
      return rangeObj
    })

    let workUnitId = null
    if (currentWorkUnitToken.length > 0 && allWorkUnits.data) {
      const findWorkUnitId = (nodes, targetToken) => {
        for (const node of nodes) {
          if (node.value === targetToken) return node.id
          if (node.children?.length > 0) {
            const found = findWorkUnitId(node.children, targetToken)
            if (found) return found
          }
        }
        return null
      }
      workUnitId = findWorkUnitId(allWorkUnits.data, currentWorkUnitToken[0])
    }

    let currency_id = null
    if (formData.site_currency) {
      if (formData.site_currency === 'all') {
        currency_id = null
      } else {
        const currency = currencies.data?.find(c => c.short_name === formData.site_currency)
        currency_id = currency?.id || null
      }
    }

    const payload = {
      code: formData.name,
      name: formData.name,
      description: formData.site_description,
      work_unit_id: workUnitId,
      product_type_id: formData.product_type,
      exclusive: record.exclusive !== undefined ? record.exclusive : false,
      coupon_based: formData.apply_coupon || false,
      services_included: formData.required_services || [],
      applied_on: appliedOnMap[formData.application_value] || formData.application_value,
      pricing_rules: {
        currency_code: formData.range_currency,
        ranges: ranges,
      },
      starts_at: formData.validity_start
        ? dayjs(formData.validity_start).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null,
      ends_at: formData.validity_end
        ? dayjs(formData.validity_end).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
        : null,
      check_in: formData.service_start_from
        ? formData.service_start_from.format
          ? formData.service_start_from.format('YYYY-MM-DD')
          : formData.service_start_from
        : null,
      check_out: formData.service_end_to
        ? formData.service_end_to.format
          ? formData.service_end_to.format('YYYY-MM-DD')
          : formData.service_end_to
        : null,
      currency_id: currency_id,
      payment_method: formData.payment_method || null,
      terms_and_conditions_url: formData.promotion_conditions || null,
    }

    if (formData.apply_coupon && formData.coupon_code) {
      const couponData = {
        code: formData.coupon_code.toUpperCase(),
        usage_limit: parseInt(formData.total_quantity, 10) || null,
      }
      
      if (record.coupons && record.coupons.length > 0 && record.coupons[0].id) {
        couponData.id = record.coupons[0].id
      }
      
      payload.coupons = [couponData]
    }

    const productTypeObj = productTypes.find(pt => pt.id === formData.product_type)
    if (productTypeObj?.ref_table_detail === 'air_detail') {
      if (formData.airline_code?.length > 0 || formData.destination_type_id) {
        const product_rules = {}
        
        if (formData.airline_code && formData.airline_code.length > 0) {
          const hasAllAirlines = formData.airline_code.some(code => 
            code.toUpperCase() === 'YY' || code.toUpperCase() === 'ALL' || code.toUpperCase() === 'TODAS'
          )
          if (hasAllAirlines) {
            product_rules.airline = null
          } else {
            product_rules.airline = formData.airline_code
          }
        }
        
        if (formData.destination_type_id) {
          const destType = destinationTypes.data?.find(dt => dt.id === formData.destination_type_id)
          if (destType && (destType.name.toLowerCase() === 'todos' || destType.name.toLowerCase() === 'all')) {
            product_rules.destination_type = null
          } else if (destType) {
            product_rules.destination_type = destType.name
          }
        }
        
        payload.product_rules = product_rules
      }
    } else {
      payload.product_rules = null
    }

    return payload
  }

  const onSubmit = async (formData) => {
    try {
      const payload = transformFormData(formData)
      await update(
        'promotion',
        {
          id: record.id,
          data: payload,
          previousData: record,
        },
        { mutationMode: 'pessimistic', onSuccess, onError }
      )
    } catch (error) {
      console.error('Error al actualizar promoción:', error)
      onError()
    }
  }

  if (isLoading || !transformedRecord) {
    return (
      <CardCommon title={translate('ra.title.update_promotion')}>
        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' width={'100%'} height={40} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' width={'100%'} height={40} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant='rectangular' width={'100%'} height={40} />
          </Grid>
        </Grid>
      </CardCommon>
    )
  }

  return (
    <Edit {...props} actions={null}>
      <CardCommon title={translate('ra.title.update_promotion')}>
        <SimpleForm 
          record={transformedRecord}
          toolbar={<CardCrudBottomButtons btnSave={true} btnDelete={true} />} 
          onSubmit={onSubmit}
        >
          <Grid container spacing={3}>
            {/* INFORMACIÓN GENERAL */}
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                {translate('ra.title.general_information')}
              </Typography>
            </Grid>
            
            <EditInputsWrap label={translate('ra.field.promotion_name')} md={4}>
              <TextInput source='name' label='' fullWidth disabled />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.site_description')} md={4}>
              <TextInput source='site_description' label='' fullWidth />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.apply_coupon')} md={4}>
              <BooleanInput
                label={translate('ra.action.apply')}
                source='apply_coupon'
                fullWidth
              />
            </EditInputsWrap>

            {/* DATOS DEL CUPÓN */}
            <FormDataConsumer>
              {({ formData }) =>
                formData.apply_coupon && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        backgroundColor: 'rgba(194, 194, 194, 0.13)',
                        borderRadius: 1,
                        p: 2,
                        mt: 1
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                            {translate('ra.title.coupon_data')}
                          </Typography>
                        </Grid>
                        
                        <EditInputsWrap label={translate('ra.field.coupon_code')} md={6}>
                          <TextInput 
                            source='coupon_code' 
                            label='' 
                            fullWidth 
                            disabled
                            sx={{ backgroundColor: 'white' }}
                            format={v => v?.toUpperCase()}
                            parse={v => v?.toUpperCase()}
                          />
                        </EditInputsWrap>

                        <EditInputsWrap label={translate('ra.field.total_quantity')} md={6}>
                          <TextInput 
                            source='total_quantity' 
                            label='' 
                            fullWidth 
                            type="number"
                            sx={{ backgroundColor: 'white' }} 
                          />
                        </EditInputsWrap>
                      </Grid>
                    </Box>
                  </Grid>
                )
              }
            </FormDataConsumer>

            {/* CAMPOS ADICIONALES */}
            <Modal open={openWorkUnitModal} onClose={handleCloseWorkUnitModal}>
              <Box sx={modalStyle}>
                <Typography variant='h6' style={{ marginBottom: '20px', fontWeight: 600 }}>
                  {translate('ra.title.select_work_unit')}
                </Typography>
                <Typography
                  variant='body2'
                  style={{ marginBottom: '20px', marginLeft: '10px', color: '#666', fontSize: '13px' }}
                >
                  {translate('ra.message.instructive_work_unit_select')}
                </Typography>
                <TreeViewCheck
                  checked={currentWorkUnitToken}
                  setChecked={setCurrentWorkUnitToken}
                  nodes={allWorkUnits.data || []}
                  setExpanded={setExpanded}
                  expanded={expanded}
                  onlyOne={true}
                  restrictToLevel={false}
                />
              </Box>
            </Modal>

            <EditInputsWrap label={translate('ra.field.work_unit')} md={6}>
              {allWorkUnits.isLoading ? (
                <Skeleton variant='rectangular' width={'100%'} height={50} />
              ) : (
                <Button
                  sx={{
                    width: '100%',
                    height: 'auto',
                    minHeight: '40px',
                  }}
                  onClick={handleOpenWorkUnitModal}
                  variant='outlined'
                >
                  {currentWorkUnitName?.length ? currentWorkUnitName : translate('ra.action.select')}
                </Button>
              )}
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.product_type')} md={6}>
              {loadingProductType ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  source='product_type'
                  label=''
                  fullWidth
                  options={productTypes || []}
                  optionsText={['long_name']}
                  optionValue='id'
                  displayEmpty
                  onChange={(e) => setSelectedProductType(e?.target?.value)}
                  renderValue={(selected) => {
                    if (!selected) {
                      return translate('ra.action.select')
                    }
                    const option = productTypes.find((opt) => opt.id === selected)
                    return option ? option.long_name : selected
                  }}
                />
              )}
            </EditInputsWrap>

            {/* CAMPOS CONDICIONALES PARA AÉREOS */}
            <FormDataConsumer>
              {({ formData }) => {
                const productTypeObj = productTypes.find(pt => pt.id === formData.product_type)
                const isAirProduct = productTypeObj?.ref_table_detail === 'air_detail'
                
                return isAirProduct ? (
                  <>
                    <EditInputsWrap label={`${translate('ra.field.airline')} (${translate('ra.message.optional')})`} md={6}>
                      <AirlineSelectField airAirlines={airAirlines} loadingAirAirlines={loadingAirAirlines} />
                    </EditInputsWrap>

                    <EditInputsWrap label={translate('ra.field.destination_type')} md={6}>
                      <DestinationTypeField destinationTypes={destinationTypes} />
                    </EditInputsWrap>
                  </>
                ) : null
              }}
            </FormDataConsumer>

            <EditInputsWrap label={translate('ra.field.required_services')} md={6}>
              <RequiredServicesField requiredServicesList={requiredServicesList} />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.application_value')} md={6}>
              <SelectInput
                source='application_value'
                label=''
                fullWidth
                options={applicationValues}
                optionsText={['name']}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return translate('ra.action.select')
                  }
                  const option = applicationValues.find((opt) => opt.id === selected)
                  return option ? option.name : selected
                }}
              />
            </EditInputsWrap>


            {/* TIPO Y MONTO DE DESCUENTO */}
            <Grid item xs={12}>
              <Typography variant='h6' sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                {translate('ra.title.discount_type_amount')}
              </Typography>
            </Grid>

            <EditInputsWrap label={translate('ra.field.range_currency')} md={6}>
              <SelectInput
                source='range_currency'
                label=''
                fullWidth
                options={rangeCurrencies}
                optionsText={['name']}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected) {
                    return translate('ra.action.select')
                  }
                  const option = rangeCurrencies.find((opt) => opt.id === selected)
                  return option ? option.name : selected
                }}
              />
            </EditInputsWrap>

            {/* RANGOS */}
            <Grid item xs={12}>
              <RangesArray initialRanges={transformedRecord?.ranges} />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* FECHAS */}
            <EditInputsWrap label={translate('ra.field.validity_start')} md={3}>
              <DatePickers 
                source='validity_start' 
                viewsShow={false} 
                defaultHour={0} 
                defaultMinute={0} 
                minDate={dayjs().startOf('day')}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.validity_end')} md={3}>
              <DatePickers 
                source='validity_end' 
                viewsShow={false} 
                defaultHour={23} 
                defaultMinute={59} 
                minDate={dayjs().startOf('day')}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.service_start_from')} md={3}>
              <DatePickers 
                source='service_start_from' 
                viewsShow={false} 
                minDate={dayjs().startOf('day')}
              />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.service_end_to')} md={3}>
              <DatePickers 
                source='service_end_to' 
                viewsShow={false} 
                minDate={dayjs().startOf('day')}
              />
            </EditInputsWrap>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* MONEDA SITIO Y MÉTODO DE PAGO */}
            <EditInputsWrap label={translate('ra.field.site_currency')} md={6}>
              <SiteCurrencyField destinationTypes={destinationTypes} siteCurrencies={siteCurrencies} />
            </EditInputsWrap>

            <EditInputsWrap label={translate('ra.field.payment_method_optional')} md={6}>
              <SelectInput
                source="payment_method"
                label=""
                fullWidth
                options={paymentMethods}
                optionsText={['name']}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected === '') {
                    return <span style={{ color: '#999' }}>Seleccionar</span>;
                  }
                  return paymentMethods.find(p => p.id === selected)?.name;
                }}
                sx={{ backgroundColor: 'white' }}
              />
            </EditInputsWrap>

            {/* CONDICIONES DE LA PROMOCIÓN */}
            <EditInputsWrap label={translate('ra.field.promotion_conditions')} md={12}>
              <TextInput
                source="promotion_conditions"
                label=""
                placeholder={translate('ra.field.redirect_url_placeholder')}
                sx={{ backgroundColor: 'white' }}
              />
            </EditInputsWrap>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}